import { type FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber, type CountryCode, type ParseError } from 'libphonenumber-js';
import { Controller, useFormContext } from 'FrontRoyalReactHookForm';
import useLocalizedCountries from 'Countries/countriesUtils';
import { formattedTelephonePlaceholder } from 'Telephone';
import { Input as HeroUiInput } from '@heroui/react';
import { Autocomplete, type AutocompleteOption } from 'Autocomplete';
import { Text } from 'Text';
import 'Telephone/locales/telephone/tel_input-en.json';
import { type PhoneInputProps } from './PhoneInput.types';

// A country-code picker plus a national-number field, which together write a single E.164 string
// into the surrounding react-hook-form. The validation and formatting behavior matches the Angular
// `<tel-input>` directive and the MUI `FrontRoyalMaterialUiForm/TelephoneInput` (both of which build
// on libphonenumber-js): the number is reformatted as you type, an invalid number surfaces an inline
// message, and clearing the field stores null rather than an unparseable string.
//
// Unlike the MUI version this has no extension field — no current caller collects one.
export const PhoneInput: FC<PhoneInputProps> = memo(
    ({ name, label, defaultCountryCode, isDisabled, isRequired, errorMessage }) => {
        const { t } = useTranslation('back_royal');
        const { control, setFieldValue } = useFormContext();

        const countries = useLocalizedCountries().filter(country => country.text.length > 0);
        const countryOptions: AutocompleteOption[] = useMemo(
            () =>
                countries.map(country => ({
                    value: country.value,
                    label: `${country.text} (${country.telPrefix})`,
                })),
            [countries],
        );

        return (
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <PhoneInputControl
                        name={name}
                        label={label}
                        defaultCountryCode={defaultCountryCode}
                        isDisabled={isDisabled}
                        isRequired={isRequired}
                        errorMessage={errorMessage}
                        countryOptions={countryOptions}
                        countries={countries}
                        value={field.value as string | null}
                        setFieldValue={setFieldValue}
                        t={t}
                    />
                )}
            />
        );
    },
);

type Country = { value: string; text: string; telPrefix: string };

type ControlProps = PhoneInputProps & {
    countryOptions: AutocompleteOption[];
    countries: Country[];
    value: string | null;
    setFieldValue: (
        name: string,
        value: string | null,
        options?: { shouldDirty?: boolean; shouldTouch?: boolean; shouldValidate?: boolean },
    ) => void;
    t: (key: string) => string;
};

// Split out from PhoneInput so the country/national-number UI state is created once per mounted
// field rather than on every Controller render.
function PhoneInputControl({
    name,
    label,
    defaultCountryCode,
    isDisabled,
    isRequired,
    errorMessage,
    countryOptions,
    countries,
    value,
    setFieldValue,
    t,
}: ControlProps) {
    // Derive the initial country and displayed national number from whatever E.164 value the form
    // started with, falling back to the caller's default country.
    const [countryCode, setCountryCode] = useState<CountryCode>(() => {
        if (value) {
            try {
                const parsed = parsePhoneNumber(value);
                if (parsed.country) return parsed.country;
            } catch {
                // Not parseable on its own — fall through to the default country below.
            }
        }
        return (defaultCountryCode || 'US') as CountryCode;
    });

    const [nationalNumber, setNationalNumber] = useState(() => {
        if (!value) return '';
        try {
            return parsePhoneNumber(value).formatNational();
        } catch {
            return value;
        }
    });

    const [isInvalid, setIsInvalid] = useState(false);

    // Only ever stores a valid E.164 string, or null. An invalid number stores null rather than the
    // raw text so the consumer's `required` rule rejects it, as the Angular directive's
    // $setValidity('invalid_phone') did — a shape check alone would accept plenty of invalid numbers.
    const validateAndStore = (number: string, code: CountryCode) => {
        if (!number.trim()) {
            setIsInvalid(false);
            setFieldValue(name, null);
            return;
        }

        try {
            const parsed = parsePhoneNumber(number, code);
            if (parsed.isValid()) {
                // formatNational, matching the Angular tel-input directive: "(571) 483-8002" for US.
                setNationalNumber(parsed.formatNational());
                setIsInvalid(false);
                setFieldValue(name, parsed.number);
                return;
            }
            setIsInvalid(true);
            setFieldValue(name, null);
        } catch (error) {
            setIsInvalid((error as ParseError).message !== 'NOT_A_NUMBER');
            setFieldValue(name, null);
        }
    };

    useCountryFromCaller(defaultCountryCode, countryCode, nextCode => {
        setCountryCode(nextCode);
        validateAndStore(nationalNumber, nextCode);
    });

    useRejectUnparseableInitialValue(value, setIsInvalid, () => clearField(name, setFieldValue));

    const onCountryChange = (nextValue: string) => {
        const nextCode = nextValue as CountryCode;
        setCountryCode(nextCode);
        validateAndStore(nationalNumber, nextCode);
    };

    const onNumberChange = (nextNumber: string) => {
        setNationalNumber(nextNumber);
        validateAndStore(nextNumber, countryCode);
    };

    const telPrefix = countries.find(country => country.value === countryCode)?.telPrefix ?? '';

    return (
        <div className="flex flex-col gap-1">
            {label ? <FieldLabel label={label} isRequired={isRequired} /> : null}

            {/* The country control stays narrow, showing only the dialing code as the Angular
                tel-input did; the country names live in its dropdown, which sizes itself to them. */}
            <div className="flex items-start gap-2">
                <Autocomplete
                    options={countryOptions}
                    value={countryCode}
                    onValueChange={onCountryChange}
                    aria-label={t('telephone.tel_input.country_code')}
                    isDisabled={isDisabled}
                    placeholder={telPrefix}
                    selectedDisplayValue={telPrefix}
                    className="w-28 shrink-0"
                />

                <div className="min-w-0 grow">
                    <PhoneNumberField
                        countryCode={countryCode}
                        isDisabled={isDisabled}
                        // HeroUI only renders errorMessage while isInvalid, so a consumer-supplied
                        // message has to flip it too.
                        isInvalid={isInvalid || !!errorMessage}
                        isRequired={isRequired}
                        nationalNumber={nationalNumber}
                        onNumberChange={onNumberChange}
                        errorMessage={isInvalid ? t('telephone.tel_input.invalid_phone') : errorMessage}
                    />
                </div>
            </div>
        </div>
    );
}

// The caller's country is live (the mailing address Country field drives it), so follow changes to it
// and re-validate, as the Angular directive's $watch on countryCode did. Skipped on mount so the
// country parsed out of an existing value isn't overwritten by the caller's default.
function useCountryFromCaller(
    defaultCountryCode: CountryCode | null | undefined,
    countryCode: CountryCode,
    onChange: (nextCode: CountryCode) => void,
) {
    const skipMount = useRef(true);

    useEffect(() => {
        if (skipMount.current) {
            skipMount.current = false;
            return;
        }
        const nextCode = (defaultCountryCode || 'US') as CountryCode;
        if (nextCode !== countryCode) onChange(nextCode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultCountryCode]);
}

// Flags a stored number that isn't a valid phone number (a legacy value written before this was
// checked) and drops it, so the consumer's required rule rejects it just as it would an invalid edit.
// Reporting it without clearing would show an error beside an enabled submit button.
// Drops the value without dirtying the form, so an untouched form isn't reported as edited.
function clearField(name: string, setFieldValue: ControlProps['setFieldValue']) {
    setFieldValue(name, null, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
}

function useRejectUnparseableInitialValue(
    value: string | null,
    setIsInvalid: (invalid: boolean) => void,
    onInvalid: () => void,
) {
    useEffect(() => {
        if (!value) return;
        let invalid = true;
        try {
            invalid = !parsePhoneNumber(value).isValid();
        } catch {
            invalid = true;
        }
        if (!invalid) return;
        setIsInvalid(true);
        onInvalid();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

// Uses HeroUI's Input, not the component-library one: that wrapper binds to an RHF field and lets
// field.value win over `value`, but the displayed national number differs from the stored E.164.
// classNames are copied from Input/Input.tsx to match the design system — keep them in sync.
function PhoneNumberField({
    countryCode,
    isDisabled,
    isInvalid,
    isRequired,
    nationalNumber,
    onNumberChange,
    errorMessage,
}: {
    countryCode: CountryCode;
    isDisabled?: boolean;
    isInvalid: boolean;
    isRequired?: boolean;
    nationalNumber: string;
    onNumberChange: (value: string) => void;
    errorMessage?: string;
}) {
    return (
        <HeroUiInput
            type="tel"
            aria-label="telephone-input"
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            isRequired={isRequired}
            errorMessage={errorMessage}
            placeholder={formattedTelephonePlaceholder(countryCode)}
            value={nationalNumber}
            onChange={event => onNumberChange(event.target.value)}
            classNames={{
                // Same height as the country Autocomplete beside it and the rest of the form's inputs.
                inputWrapper: 'bg-slate-grey-50 hover:bg-slate-grey-100 rounded-7 h-14 min-h-10',
                input: 'text-slate-grey group-data-[has-value=true]:text-slate-grey text-base',
                errorMessage: 'text-red-500',
            }}
        />
    );
}

// This field's label sits above the control rather than inside it, so the required marker is drawn
// here rather than coming from HeroUI.
function FieldLabel({ label, isRequired }: { label: string; isRequired?: boolean }) {
    return (
        <Text as="div" color="black" weight="semibold" className="text-sm">
            {label}
            {isRequired ? <span className="ms-0.5 text-danger">*</span> : null}
        </Text>
    );
}
